import { Link } from 'react-router';
import type { PublicListingDto } from 'shared';
import { CoverImage } from '../../components/CoverImage.js';

/**
 * Image (reuses `CoverImage`, the app's one existing photo/placeholder
 * component) + price + a bed/bath/city subtitle line, per the reference
 * screenshot's card shape.
 *
 * The whole card is one link to the listing's own detail page
 * (`/listings/:id`, `PublicListingDetail.tsx`) — clicking a listing is
 * meant to go there first, with Apply and Contact living as prominent
 * actions on that page rather than a shortcut off the card itself.
 */

/** Display formatting only, matching the convention used across staff reports
 * (e.g. ArAging.tsx) — the underlying value is still the server's decimal
 * string, never arithmetic performed on it. */
export function formatPrice(price: string): string {
  return Number(price).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function ListingCard({ listing }: { listing: PublicListingDto }) {
  const specs = [`${listing.bedrooms} bd`, `${listing.bathrooms} ba`, listing.city || null].filter(
    Boolean,
  );

  return (
    <Link to={`/listings/${listing.id}`} className="listing-card">
      <CoverImage
        src={listing.photoUrl}
        alt={listing.addressLine1 || 'Rental listing'}
        variant="card"
      />
      <div className="listing-card__body">
        <p className="listing-card__price">${formatPrice(listing.price)}/month</p>
        <p className="listing-card__specs">{specs.join(' | ')}</p>
        {listing.description ? (
          <p className="listing-card__description">{listing.description}</p>
        ) : null}
      </div>
    </Link>
  );
}
